import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { Alert, Button, Checkbox, Modal, Progress, Space, Typography } from "antd";
import { DcatCatalogs } from "../../redux";

const { Paragraph, Text, Title } = Typography;
const DCAT_DATASET = "http://www.w3.org/ns/dcat#dataset";
const DCAT_DISTRIBUTION = "http://www.w3.org/ns/dcat#distribution";
const SKSA_WIKI = "http://purl.semanticpro.org/ontologies/sksa/wiki";

function DeleteCatalog({ open, onClose, onDeleted, dispatch, graphURI, catalogURI }) {
  const [wholeGraph, setWholeGraph] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isDone, setIsDone] = useState(false);
  const [progress, setProgress] = useState(0);
  const [status, setStatus] = useState("");
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) return;
    setWholeGraph(false); setIsDeleting(false); setIsDone(false);
    setProgress(0); setStatus(""); setError(null);
  }, [open]);

  async function deleteIRI(iri) {
    if (iri) await dispatch(DcatCatalogs.delete(iri, graphURI));
  }

  async function deleteCatalogContents() {
    setStatus("Получение содержимого каталога…");
    const catalog = await DcatCatalogs.loadSubject(catalogURI, graphURI);
    const datasets = objectsByPredicate(catalog.bindings, DCAT_DATASET);
    const total = Math.max(datasets.length + 1, 1);

    for (let index = 0; index < datasets.length; index += 1) {
      const assetIRI = datasets[index];
      setStatus(`Получение содержимого актива ${index + 1} из ${datasets.length}…`);
      const asset = await DcatCatalogs.loadSubject(assetIRI, graphURI);
      const distributions = objectsByPredicate(asset.bindings, DCAT_DISTRIBUTION);
      const wikies = objectsByPredicate(asset.bindings, SKSA_WIKI);

      for (const distributionIRI of distributions) {
        setStatus(`Удаление распространения ${distributionIRI}…`);
        await deleteIRI(distributionIRI);
      }
      for (const wikiIRI of wikies) {
        setStatus(`Удаление wiki-статьи ${wikiIRI}…`);
        await deleteIRI(wikiIRI);
      }

      setStatus(`Удаление актива ${assetIRI}…`);
      await deleteIRI(assetIRI);
      setProgress(Math.round(((index + 1) / total) * 100));
    }

    setStatus(`Удаление каталога ${catalogURI}…`);
    await deleteIRI(catalogURI);
    setProgress(100);
  }

  async function deleteWholeGraph() {
    setStatus("Получение списка объектов графа…");
    const subjects = await dispatch(DcatCatalogs.loadGraphSubjects(graphURI));
    const total = Math.max(subjects.length, 1)+1;
    for (let index = 0; index < subjects.length; index += 1) {
      setStatus(`Удаление объекта ${index + 1} из ${subjects.length}…`);
//Не будем вызывать удаления, т.к. удаление графа всё удалит
//Оставим на будущее, если надо будет удалять представления и содержимое      
//      await deleteIRI(subjects[index]);
      setProgress(Math.round(((index + 1) / total) * 100));
    }
    //Удаление графа CLEAR GRAPH <http://example.org/myGraph>
    setStatus(`Удаление графа ${graphURI} …`);
    await dispatch(DcatCatalogs.deleteGrpah(graphURI));
    setProgress(100);
  }

  async function handleDelete() {
    setIsDeleting(true); setIsDone(false); setProgress(0); setError(null);
    try {
      if (wholeGraph) await deleteWholeGraph();
      else await deleteCatalogContents();
      setStatus(wholeGraph ? "Граф удалён." : "Каталог удалён.");
      setIsDone(true);
      onDeleted?.({ catalogURI, graphURI, wholeGraph });
    } catch (e) {
      setError(e?.message || String(e)); setStatus("");
    } finally {
      setIsDeleting(false);
    }
  }

  function handleClose() {
    if (!isDeleting) onClose?.();
  }

  return (
    <Modal
      title={<Title level={5} style={{ margin: 0 }}>Удаление каталога</Title>}
      open={open}
      onCancel={handleClose}
      closable={!isDeleting}
      maskClosable={!isDeleting}
      footer={<Space>
        {!isDone ? <Button onClick={handleClose} disabled={isDeleting}>Отмена</Button> : null}
        <Button danger={!isDone} type="primary" loading={isDeleting}
          disabled={isDeleting || !catalogURI || !graphURI}
          onClick={isDone ? handleClose : handleDelete}>
          {isDone ? "Закрыть" : "Удалить"}
        </Button>
      </Space>}
    >
      <Space orientation="vertical" size="middle" style={{ width: "100%" }}>
        <Alert type="warning" showIcon message="Удаление необратимо"
          description={<Paragraph style={{ marginBottom: 0 }}>
            Перед удалением рекомендуется экспортировать каталог или обратиться к администратору для его архивирования.
          </Paragraph>}/>
        <Text>Будут удалены распространения и wiki-статьи активов, затем сами активы и каталог.</Text>
        <Space orientation="vertical" size={8} style={{ width: "100%" }}>
          <Checkbox checked={false} disabled>Включить удаление визуальных представлений</Checkbox>
          <Checkbox checked={false} disabled style={{ marginLeft: 24 }}>Включить удаление графов, указанных в представлениях</Checkbox>
          <Checkbox checked={wholeGraph} disabled={isDeleting || isDone}
            onChange={event => setWholeGraph(event.target.checked)}>Весь граф</Checkbox>
        </Space>
        {wholeGraph ? <Alert type="error" showIcon message="Будет удалено всё содержимое выбранного графа" description={graphURI}/> : null}
        {isDeleting || isDone ? <Space orientation="vertical" style={{ width: "100%" }}>
          <Progress percent={progress} status={isDone ? "success" : "active"}/>
          {status ? <Text>{status}</Text> : null}
        </Space> : null}
        {error ? <Alert type="error" showIcon message="Ошибка удаления" description={error}/> : null}
      </Space>
    </Modal>
  );
}

function objectsByPredicate(triples, predicate) {
  return [...new Set((Array.isArray(triples) ? triples : [])
    .filter(item => item.predicat?.value === predicate && item.object?.type === "uri")
    .map(item => item.object.value).filter(Boolean))];
}

const mapStateToProps = (state, ownProps) => ({
  ...ownProps,
  graphURI: ownProps.graphURI ?? ownProps.graph ?? state.vset?.graph?.signed,
  catalogURI: ownProps.catalogURI ?? ownProps.catalog,
});

export default connect(mapStateToProps)(DeleteCatalog);
